import {
  FileDown,
  Trash2,
  Archive,
  Mail,
  Tag,
  CheckCircle,
  XCircle,
  RefreshCw,
  Download,
  Copy,
  Edit,
  Eye,
  Users,
  Star,
  Heart,
  Flag,
  Shield
} from 'lucide-react'
import type { BirhausBulkAction } from './types'
import { 
  createSimpleAction, 
  createUndoableAction, 
  isUndoableAction 
} from '../../types/bulk-actions'

/**
 * BIRHAUS Bulk Action Presets
 * Pre-configured actions following BIRHAUS principles
 * - Spanish-first labels
 * - Undo-over-confirm patterns
 * - Miller's Law compliance
 * - Accessibility-first design
 */

/**
 * Export action with format options
 */
export const createExportAction = <T>(
  onExport: (items: T[], format?: string) => Promise<void>,
  options: {
    formats?: ('csv' | 'xlsx' | 'json' | 'pdf')[]
    defaultFormat?: string
  } = {}
): BirhausBulkAction<T> => createSimpleAction({
  id: 'export',
  labelEs: 'Exportar',
  labelEn: 'Export',
  icon: FileDown,
  variant: 'secondary',
  onClick: async (items) => {
    await onExport(items, options.defaultFormat || 'csv')
  },
  minItems: 1,
  validate: (items) => ({
    valid: items.length > 0,
    message: items.length === 0 ? 'Selecciona al menos un elemento para exportar' : undefined
  })
})

/**
 * Delete action with undo support (BIRHAUS undo-over-confirm)
 */
export const createDeleteAction = <T>(
  onDelete: (items: T[]) => Promise<{ undoAction: () => Promise<void> }>,
  count?: number
): BirhausBulkAction<T> => createUndoableAction({
  id: 'delete',
  labelEs: 'Eliminar',
  labelEn: 'Delete',
  icon: Trash2,
  variant: 'destructive',
  onClick: onDelete,
  undoTimeout: 10000, // 10 seconds to undo
  clearSelectionOnComplete: true,
  minItems: 1,
  validate: (items) => ({
    valid: items.length > 0,
    message: items.length === 0 ? 'Selecciona elementos para eliminar' : undefined
  })
})

/**
 * Archive action with undo support
 */
export const createArchiveAction = <T>(
  onArchive: (items: T[]) => Promise<{ undoAction: () => Promise<void> }>,
  options: { undoTimeout?: number } = {}
): BirhausBulkAction<T> => createUndoableAction({
  id: 'archive',
  labelEs: 'Archivar',
  labelEn: 'Archive',
  icon: Archive,
  variant: 'secondary',
  onClick: onArchive,
  undoTimeout: options.undoTimeout || 15000, // 15 seconds for archive operations
  minItems: 1
})

/**
 * Email/notification action
 */
export const createEmailAction = <T>(
  onEmail: (items: T[]) => Promise<void>,
  options: { 
    label?: { es: string; en?: string }
    template?: string 
  } = {}
): BirhausBulkAction<T> => createSimpleAction({
  id: 'email',
  labelEs: options.label?.es || 'Enviar Email',
  labelEn: options.label?.en || 'Send Email',
  icon: Mail,
  variant: 'secondary',
  onClick: onEmail,
  minItems: 1,
  maxItems: 50, // Limit for email sending
  validate: (items) => ({
    valid: items.length > 0 && items.length <= 50,
    message: items.length === 0 
      ? 'Selecciona destinatarios para el email'
      : items.length > 50 
        ? 'Máximo 50 destinatarios por email'
        : undefined
  })
})

/**
 * Tag/label action
 */
export const createTagAction = <T>(
  onTag: (items: T[], tag?: string) => Promise<{ undoAction: () => Promise<void> }>,
  tag?: string
): BirhausBulkAction<T> => createUndoableAction({
  id: `tag-${tag || 'generic'}`,
  labelEs: tag ? `Etiquetar como "${tag}"` : 'Etiquetar',
  labelEn: tag ? `Tag as "${tag}"` : 'Tag',
  icon: Tag,
  variant: 'secondary',
  onClick: async (items) => onTag(items, tag),
  undoTimeout: 8000,
  minItems: 1
})

/**
 * Approve action
 */
export const createApproveAction = <T>(
  onApprove: (items: T[]) => Promise<{ undoAction: () => Promise<void> }>
): BirhausBulkAction<T> => createUndoableAction({
  id: 'approve',
  labelEs: 'Aprobar',
  labelEn: 'Approve',
  icon: CheckCircle,
  variant: 'primary',
  onClick: onApprove,
  undoTimeout: 12000,
  minItems: 1
})

/**
 * Reject action
 */
export const createRejectAction = <T>(
  onReject: (items: T[]) => Promise<{ undoAction: () => Promise<void> }>
): BirhausBulkAction<T> => createUndoableAction({
  id: 'reject',
  labelEs: 'Rechazar',
  labelEn: 'Reject',
  icon: XCircle,
  variant: 'destructive',
  onClick: onReject,
  undoTimeout: 12000,
  minItems: 1
})

/**
 * Status update action
 */
export const createStatusUpdateAction = <T>(
  onUpdateStatus: (items: T[], status: string) => Promise<{ undoAction: () => Promise<void> }>,
  status: string,
  statusLabel?: { es: string; en?: string }
): BirhausBulkAction<T> => createUndoableAction({
  id: `status-${status}`,
  labelEs: statusLabel?.es || `Cambiar a ${status}`,
  labelEn: statusLabel?.en || `Change to ${status}`,
  icon: RefreshCw,
  variant: 'secondary',
  onClick: (items) => onUpdateStatus(items, status),
  undoTimeout: 10000,
  minItems: 1
})

/**
 * Download action
 */
export const createDownloadAction = <T>(
  onDownload: (items: T[]) => Promise<void>
): BirhausBulkAction<T> => createSimpleAction({
  id: 'download',
  labelEs: 'Descargar',
  labelEn: 'Download',
  icon: Download,
  variant: 'secondary',
  onClick: onDownload,
  minItems: 1,
  maxItems: 20 // Limit concurrent downloads
})

/**
 * Copy action
 */
export const createCopyAction = <T>(
  onCopy: (items: T[]) => Promise<void>
): BirhausBulkAction<T> => createSimpleAction({
  id: 'copy',
  labelEs: 'Copiar',
  labelEn: 'Copy',
  icon: Copy,
  variant: 'secondary',
  onClick: onCopy, // Copy doesn't modify original data
  minItems: 1
})

/**
 * Edit action
 */
export const createEditAction = <T>(
  onEdit: (items: T[]) => Promise<{ undoAction: () => Promise<void> }>,
  options: { 
    singleOnly?: boolean 
    maxItems?: number 
  } = {}
): BirhausBulkAction<T> => createUndoableAction({
  id: 'edit',
  labelEs: 'Editar',
  labelEn: 'Edit',
  icon: Edit,
  variant: 'secondary',
  onClick: onEdit,
  undoTimeout: 15000,
  minItems: 1,
  maxItems: options.singleOnly ? 1 : (options.maxItems || 10),
  validate: (items) => {
    if (options.singleOnly && items.length > 1) {
      return {
        valid: false,
        message: 'Solo se puede editar un elemento a la vez'
      }
    }
    return { valid: items.length > 0 }
  }
})

/**
 * Assign action (for user/group assignments)
 */
export const createAssignAction = <T>(
  onAssign: (items: T[], assignee?: string) => Promise<{ undoAction: () => Promise<void> }>,
  assignee?: string
): BirhausBulkAction<T> => createUndoableAction({
  id: `assign-${assignee || 'generic'}`,
  labelEs: assignee ? `Asignar a ${assignee}` : 'Asignar',
  labelEn: assignee ? `Assign to ${assignee}` : 'Assign',
  icon: Users,
  variant: 'secondary',
  onClick: async (items) => onAssign(items, assignee),
  undoTimeout: 10000,
  minItems: 1
})

/**
 * Favorite/star action
 */
export const createFavoriteAction = <T>(
  onFavorite: (items: T[], favorite: boolean) => Promise<{ undoAction: () => Promise<void> }>,
  isFavorite = true
): BirhausBulkAction<T> => createUndoableAction({
  id: isFavorite ? 'favorite' : 'unfavorite',
  labelEs: isFavorite ? 'Marcar como favorito' : 'Quitar de favoritos',
  labelEn: isFavorite ? 'Add to favorites' : 'Remove from favorites',
  icon: isFavorite ? Star : Star,
  variant: 'secondary',
  onClick: async (items) => onFavorite(items, isFavorite),
  undoTimeout: 8000,
  minItems: 1
})

/**
 * Hide/show visibility action
 */
export const createVisibilityAction = <T>(
  onToggleVisibility: (items: T[], visible: boolean) => Promise<{ undoAction: () => Promise<void> }>,
  makeVisible = true
): BirhausBulkAction<T> => createUndoableAction({
  id: makeVisible ? 'show' : 'hide',
  labelEs: makeVisible ? 'Mostrar' : 'Ocultar',
  labelEn: makeVisible ? 'Show' : 'Hide',
  icon: makeVisible ? Eye : Eye,
  variant: 'secondary',
  onClick: async (items) => onToggleVisibility(items, makeVisible),
  undoTimeout: 10000,
  minItems: 1
})

/**
 * Priority action
 */
export const createPriorityAction = <T>(
  onSetPriority: (items: T[], priority: 'high' | 'medium' | 'low') => Promise<{ undoAction: () => Promise<void> }>,
  priority: 'high' | 'medium' | 'low'
): BirhausBulkAction<T> => {
  const priorityLabels = {
    high: { es: 'Alta prioridad', en: 'High priority' },
    medium: { es: 'Prioridad media', en: 'Medium priority' },
    low: { es: 'Baja prioridad', en: 'Low priority' }
  }

  return createUndoableAction({
    id: `priority-${priority}`,
    labelEs: priorityLabels[priority].es,
    labelEn: priorityLabels[priority].en,
    icon: Flag,
    variant: priority === 'high' ? 'primary' : 'secondary',
    onClick: async (items) => onSetPriority(items, priority),
    undoTimeout: 10000,
    minItems: 1
  })
}

/**
 * Permission action (for access control)
 */
export const createPermissionAction = <T>(
  onUpdatePermissions: (items: T[], permission: string, grant: boolean) => Promise<{ undoAction: () => Promise<void> }>,
  permission: string,
  grant = true
): BirhausBulkAction<T> => createUndoableAction({
  id: `permission-${permission}-${grant ? 'grant' : 'revoke'}`,
  labelEs: grant ? `Otorgar ${permission}` : `Revocar ${permission}`,
  labelEn: grant ? `Grant ${permission}` : `Revoke ${permission}`,
  icon: Shield,
  variant: grant ? 'primary' : 'destructive',
  onClick: async (items) => onUpdatePermissions(items, permission, grant),
  undoTimeout: 15000, // Longer timeout for security changes
  minItems: 1
})


/**
 * Collection of all presets implementing BulkActionPresets interface
 */
export const BulkActionPresets = {
  export: createExportAction,
  delete: createDeleteAction,
  archive: createArchiveAction,
  email: createEmailAction,
  tag: createTagAction,
  approve: createApproveAction,
  reject: createRejectAction,
  updateStatus: createStatusUpdateAction
}

/**
 * Extended presets for additional functionality
 */
export const ExtendedBulkActionPresets = {
  ...BulkActionPresets,
  download: createDownloadAction,
  copy: createCopyAction,
  edit: createEditAction,
  assign: createAssignAction,
  favorite: createFavoriteAction,
  visibility: createVisibilityAction,
  priority: createPriorityAction,
  permission: createPermissionAction
}

/**
 * Factory function for creating custom bulk action configurations
 */
export function createBulkActionConfig<T>() {
  return {
    createAction: (config: Omit<BirhausBulkAction<T>, 'id'> & { id: string }): BirhausBulkAction<T> => {
      // Default to simple action if type not specified
      const actionConfig = {
        ...config,
        type: config.type || ('simple' as const),
        // Ensure Spanish-first principle
        labelEs: config.labelEs || config.id,
        // Add default validation if none provided
        validate: config.validate || ((items) => ({
          valid: items.length > 0,
          message: items.length === 0 ? 'Selecciona elementos para continuar' : undefined
        }))
      }
      
      // Use appropriate builder based on type
      if (actionConfig.type === 'undoable') {
        return createUndoableAction({
          id: actionConfig.id,
          labelEs: actionConfig.labelEs,
          labelEn: actionConfig.labelEn,
          icon: actionConfig.icon,
          variant: actionConfig.variant,
          disabled: actionConfig.disabled,
          onClick: actionConfig.onClick as any, // Type assertion needed for factory
          undoTimeout: (actionConfig as any).undoTimeout || 10000,
          clearSelectionOnComplete: actionConfig.clearSelectionOnComplete,
          minItems: actionConfig.minItems,
          maxItems: actionConfig.maxItems,
          validate: actionConfig.validate
        })
      } else {
        return createSimpleAction({
          id: actionConfig.id,
          labelEs: actionConfig.labelEs,
          labelEn: actionConfig.labelEn,
          icon: actionConfig.icon,
          variant: actionConfig.variant,
          disabled: actionConfig.disabled,
          onClick: actionConfig.onClick as any, // Type assertion needed for factory
          clearSelectionOnComplete: actionConfig.clearSelectionOnComplete,
          minItems: actionConfig.minItems,
          maxItems: actionConfig.maxItems,
          validate: actionConfig.validate
        })
      }
    },
    
    createPresetAction: (preset: keyof typeof ExtendedBulkActionPresets, ...args: any[]): BirhausBulkAction<T> => {
      const presetFunc = ExtendedBulkActionPresets[preset]
      return (presetFunc as any)(...args)
    },
    
    validateCognitiveLoad: (actions: BirhausBulkAction<T>[]) => ({
      actionsCount: actions.length,
      cognitiveLoad: actions.length <= 2 ? 'low' as const : 
                     actions.length <= 4 ? 'medium' as const : 
                     'high' as const,
      violates431Rule: actions.length > 4,
      spanishCoverage: actions.filter(a => a.labelEs && a.labelEs.length > 0).length / actions.length * 100,
      hasUndoPatterns: actions.some(a => isUndoableAction(a)),
      accessibilityScore: 85 // Base score, could be calculated based on various factors
    }),
    
    validateBirhausCompliance: (actions: BirhausBulkAction<T>[]) => ({
      millersLaw: actions.length <= 4,
      spanishFirst: actions.every(a => a.labelEs && a.labelEs.length > 0),
      undoPatterns: actions.some(a => isUndoableAction(a)),
      accessibility: actions.every(a => a.labelEs || a.labelEn), // Basic check
      cognitiveLoad: actions.length <= 2 ? 'low' as const : 
                     actions.length <= 4 ? 'medium' as const : 
                     'high' as const
    })
  }
}